import axios, { AxiosProgressEvent } from "axios";
import findLast from "lodash/findLast";
import isMobile from "is-mobile";
import isNil from "lodash/isNil";
import SessionDataPlugin from "./sessionData/VAAS.plugin";

import { AuthContext } from "./AuthProvider";
import { BinahContext } from "./BinahProvider";
import { ChatMessageRequest, FeedbackRequest, LoveResponse } from "../models/Models";
import { CheckpointID } from "../models/NavigatorModels";
import { CheckpointRecord } from "@emed-labs/ftf-datatypes/lib/SessionData/CheckpointData";
import { Collection } from "@emed-labs/resource-api-models";
import { Config } from "../environment/config";
import { DeviceConfiguration } from "@emed-labs/ftf-datatypes/lib/Config/DeviceConfig";
import { DeviceFingerprintUtils } from "../utils/DeviceFingerprintUtils";
import { DiagnosticComponent, DiagnosticReport } from "@emed-labs/ftf-datatypes/lib/SessionData/DiagnosticResult";
import { DiagnosticConfiguration, LabConfiguration } from "@emed-labs/ftf-datatypes/lib/Config/DiagnosticConfig";
import { EnvironmentContext } from "./EnvironmentProvider";
import { ExecutorUtils } from "@emed-labs/platform-utility-common-utils";
import { IImagingResult, IUserImageRecognitionRequestBody } from "@emed-labs/imaging-models";
import { InsuranceRecord } from "@emed-labs/ftf-datatypes/lib/User/UserPreferences";
import { KnownTypes } from "@emed-labs/ftf-datatypes/lib/SessionData/KnownTypes";
import { Language } from "@emed-labs/ftf-datatypes/lib/Misc/Language";
import { LoggingRecord } from "@emed-labs/ftf-datatypes/lib/SessionData/LoggingData";
import { Method, RequestConfig } from "@emed-labs/platform-utility-generic-clients";
import { ModalContext } from "./ModalProvider";
import { PatientAddress } from "@emed-labs/ftf-datatypes/lib/SessionData/PatientData";
import { PropsWithChildren, Suspense, createContext, useContext, useRef } from "react";
import { QuestionnaireDocumentMeta, QuestionnaireItem } from "@emed-labs/ui-module-questionnaire";
import { ScannerData } from "@emed-labs/ftf-datatypes/lib/SessionData/ScanData";
import { SessionEvent } from "@emed-labs/ftf-datatypes/lib/SessionData/SessionEvent";
import { SessionExtra } from "@emed-labs/ftf-datatypes/lib/SessionData/SessionExtra";
import { ThemeContext } from "./ThemeProvider";
import { Trans } from "./TranslationProvider";
import { useLogger } from "./LogProvider";
import { UserConsentData } from "@emed-labs/ftf-datatypes/lib/SessionData/UserConsentData";
import { MetricData, MetricRecord } from "@emed-labs/ftf-datatypes/lib/SessionData/MetricData";
import { VLabAPIContext } from "./VLabAPIProvider";
import { VoucherVerifyErrorTypeExtended } from "@emed-labs/ftf-datatypes/lib/Misc/Voucher";
import {
	QuestionnaireEntry,
	QuestionnaireProgressData,
	QuestionnaireReport,
	QuestionnaireReportData
} from "@emed-labs/ftf-datatypes/lib/SessionData/TreatmentData";
import {
	AttachmentRecord,
	AttachmentRecordCollection,
	ChatGPTCompletionResponse,
	CreateAttachmentRequest,
	Env,
	HealthieInsurancePlan,
	IScannerPayload,
	SaveLegacyResultRequest,
	ScannerStatusRequest,
	SessionData,
	SessionMetadata,
	SessionRecord,
	VerifyVoucherResponse,
	VoucherStatusResponse
} from "@emed-labs/virtual-lab-models";
import { ProcedureScriptUtils } from "../utils/ProcedureScriptUtils";

export type IOralToxExtra = {
	lineToObservation: { [key: string]: string };
	skuName: string;
};

export type TextractDrivingLicenceInfoResponse = {
	firstName: string;
	lastName: string;
	middleName: string;
	dateOfBirth: string;
	gender?: "m" | "f";
	height?: {
		feet: number;
		inches: number;
	};
};

export interface SessionDataContext {
	getDeviceContent: (procedure_id: string, language?: string) => Promise<DeviceConfiguration[]>;
	getLabDefinitions: (language?: string) => Promise<DiagnosticConfiguration>;
	getLabContent: (procedure_id: string, language?: string) => Promise<LabConfiguration>;
	getSessionMetadata: () => Promise<SessionMetadata>;

	getPatientAddress: (
		type: KnownTypes.PATIENT_DEFAULT_SHIPPING_ADDRESS | KnownTypes.PATIENT_PREFERRED_SHIPPING_ADDRESS | string
	) => Promise<SessionRecord<PatientAddress, typeof type>>;

	savePatientAddress: (
		type: KnownTypes.PATIENT_DEFAULT_SHIPPING_ADDRESS | KnownTypes.PATIENT_PREFERRED_SHIPPING_ADDRESS | string,
		data: PatientAddress
	) => Promise<void>;

	saveConsent: (event: Omit<UserConsentData["data"], "device_fingerprint" | "ip_address">) => Promise<void>;

	saveEventFlag: (event: SessionEvent) => Promise<void>;

	getSessionExtraByName: (name: string) => Promise<SessionRecord<SessionExtra, string>[]>;
	saveSessionExtra: (event: SessionExtra) => Promise<void>;
	saveMetric: (event: MetricRecord) => Promise<void>;

	processRecognize: (event: IUserImageRecognitionRequestBody) => Promise<IImagingResult>;
	getOralToxExtra: () => Promise<IOralToxExtra>;
	processOralTox: (
		event: IUserImageRecognitionRequestBody,
		attachmentID: string,
		procedureID: string
	) => Promise<IImagingResult>;
	getFormResponse: (formName: string) => Promise<SessionRecord<any, string>>;
	saveForm: (formName: string, data: any) => Promise<void>;
	saveLegacyResult: (event: SaveLegacyResultRequest) => Promise<void>;

	getAttachments: (attachmentID: string) => Promise<AttachmentRecordCollection>;
	getLatestAttachment: (attachmentID: string) => Promise<AttachmentRecord>;
	createAttachment: (request: CreateAttachmentRequest) => Promise<AttachmentRecord>;

	getDiagnosticComponents: (procedureID: string) => Promise<SessionRecord<DiagnosticComponent, string>[]>;
	saveDiagnosticComponent: (event: DiagnosticComponent) => Promise<void>;
	getDiagnosticResult: (procedureID: string) => Promise<SessionData<DiagnosticReport, string> | undefined>;

	saveDiagnosticResult: (event: Omit<DiagnosticReport, "submittedBy" | "certifiedBy" | "time">) => Promise<void>;

	saveChatMessage: (event: ChatMessageRequest) => Promise<void>;
	textExtractDrivingLicenceInfo: (blob: Blob) => Promise<TextractDrivingLicenceInfoResponse>;
	saveFeedback: (event: FeedbackRequest) => Promise<void>;

	saveLoggingEvent: (data: LoggingRecord) => Promise<void>;
	saveCheckpointEvent: (procedureID: string, checkpointID: CheckpointID) => Promise<void>;

	saveDeviceFingerprint: () => Promise<void>;

	loadDeviceFingerprint: () => Promise<any>;
	getInsurancePreference: () => Promise<InsuranceRecord | null>;
	saveInsurancePreference: (record: InsuranceRecord) => Promise<void>;
	getHealthieInsurancePlans: (keywords: string) => Promise<HealthieInsurancePlan[]>;
	saveScanStatus: (record: ScannerStatusRequest) => Promise<ScannerData>;

	saveScanBurn: (record: ScannerData) => Promise<void>;

	saveDataByType: (data: any, dataType: KnownTypes | string) => Promise<void>;
	getDataByType: <T>(dataType: KnownTypes | string) => Promise<SessionRecord<T>>;
	isCompleted: () => Promise<boolean>;

	saveScanRecheck: (record: ScannerData) => Promise<void>;
	saveScanRecord: (record: ScannerData) => Promise<void>;
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	callScanner: <T, R>(payload: IScannerPayload<T>) => Promise<ScannerData<R>>;
	assignAttributes: <T, R>(payload: IScannerPayload<T>) => Promise<IScannerPayload<R>>;
	saveVoucherVerify: (record: ScannerData) => Promise<VerifyVoucherResponse>;
	getVoucherStatus: () => Promise<VoucherStatusResponse>;
	saveVoucherError: (record: { error: VoucherVerifyErrorTypeExtended }) => Promise<void>;

	getProfilePrescription: () => Promise<any>;
	sendHeartbeat: () => Promise<void>;

	saveQuestionnaireProgress: (event: QuestionnaireProgressData["data"]) => Promise<void>;
	saveQuestionnaireReport: (event: QuestionnaireReport) => Promise<void>;
	getQuestionnaireResponses: (params?: {
		questionnaireID?: string;
		language?: Language;
		version?: string;
		fallbackToInProgress?: boolean;
	}) => Promise<QuestionnaireEntry[]>;

	processChatGptExtractWeight: (event: {
		base64Image?: string;
		fullTranscript?: string;
	}) => Promise<ChatGPTCompletionResponse>;
	processChatGptAudioTranscriptions: (request: {
		blob: Blob;
		fileName: string;
		contentType: string;
	}) => Promise<{ text: string }>;

	/**
	 * convenience function for translation lookup.
	 * Since class based components can have only one context, we need to pass the translation function
	 */
	lookup: TransFunction;

	authToken?: string;
	/**
	 * convenience function for class based components
	 * that can only have one context
	 */
	authContext: AuthContext;
	/**
	 * convenience function for class based components
	 * that can only have one context
	 */
	VLabAPICtx: VLabAPIContext;
	/**
	 * convenience function for class based components
	 */
	env: Env.Reader;
	/**
	 * convenience function for class based components
	 * that can only have one context
	 */
	binahContext: BinahContext;
	Modal: ModalContext;
	ThemeCtx: ThemeContext;
}

interface TransFunction {
	(
		keys: string | string[],
		options?: {
			lng?: string;
			ns?: string;
			defaultValue?: any;
			replace?: boolean;
		}
	): any;
}

export const SessionDataContext = createContext<SessionDataContext>({} as SessionDataContext);
SessionDataContext.displayName = "SessionDataContext";

export interface SessionDataPluginImperativeHandle {
	callAPI: <Request, Response>(method: Method, endpoint: string, config?: RequestConfig<Request>) => Promise<Response>;
	saveSessionData: <Record = SessionRecord<any, string>>(
		type: KnownTypes | string,
		data: Record,
		config?: RequestConfig
	) => Promise<void>;
	getSessionData: <Record = SessionRecord<any, string>>(
		dataType: string,
		config?: RequestConfig
		//@ts-ignore come back to this
	) => Promise<Collection<Record>>;
	getSessionMetadata: () => Promise<SessionMetadata>;
	getLatestSessionData: <Record = SessionRecord<any, string>>(dataType: string) => Promise<Record>;
	isCompleted: () => Promise<boolean>;
}

export interface SessionDataPlugin {
	children: any;
}

// eslint-disable-next-line no-shadow
export enum VaasPluginType {
	VAAS = "vaas"
}

export const SessionDataProvider: React.FunctionComponent<PropsWithChildren> = (props) => {
	const ThemeCtx = useContext(ThemeContext);
	const AuthCtx = useContext(AuthContext);
	const TransCtx = useContext(Trans.context);
	const EnvCtx = useContext(EnvironmentContext);
	const Modal = useContext(ModalContext);
	const VLabAPICtx = useContext(VLabAPIContext);
	const BinahCtx = useContext(BinahContext);
	const Plugin = useRef<SessionDataPluginImperativeHandle>(null);
	const logger = useLogger("providers.SessionDataProvider");

	/**
	 * This is a proof of concept of how we can load a specific plugin for provider
	 * lazily ( in the React way, React.lazy ), depending on the environment and configuration.
	 *
	 * @param method
	 * @param endpoint
	 * @param config
	 * @returns
	 */
	/* const SessionDataPlugin = lazy(() => {
		return import("./sessionData/VAAS.plugin");
	}); */

	const callAPI = <Request, Response>(
		method: Method,
		endpoint: string,
		config?: RequestConfig<Request>
	): Promise<Response> => {
		return Plugin.current!.callAPI<Request, Response>(method, endpoint, config);
	};

	const saveSessionData = <Record = SessionRecord,>(
		type: KnownTypes | string,
		data: Record,
		config?: RequestConfig
	) => {
		return Plugin.current?.saveSessionData<Record>(type, data, config);
	};

	const getSessionData = <Record = SessionRecord,>(
		dataType: string,
		config?: RequestConfig
	): Promise<Collection<Record>> => {
		return Plugin.current!.getSessionData<Record>(dataType, config);
	};
	const getLatestSessionData = <Record = SessionRecord,>(dataType: string): Promise<Record> => {
		return Plugin.current!.getLatestSessionData<Record>(dataType);
	};

	const getSessionMetadata = (): Promise<SessionMetadata> => {
		return getLatestSessionData<SessionMetadata>("SessionMetadata");
	};

	const getPatientAddress = (
		type: KnownTypes.PATIENT_DEFAULT_SHIPPING_ADDRESS | KnownTypes.PATIENT_PREFERRED_SHIPPING_ADDRESS | string
	) => {
		return getLatestSessionData<SessionRecord<PatientAddress, typeof type>>(type as string);
	};

	const savePatientAddress = (
		type: KnownTypes.PATIENT_DEFAULT_SHIPPING_ADDRESS | KnownTypes.PATIENT_PREFERRED_SHIPPING_ADDRESS | string,
		data: PatientAddress
	) => {
		return saveSessionData(type, data);
	};

	const saveConsent = (event: Omit<UserConsentData["data"], "device_fingerprint" | "ip_address">) => {
		return saveSessionData(KnownTypes.SESSION_CONSENT, event);
	};

	const saveForm = (formName: string, event: any) => {
		return saveSessionData(prefixFormType(formName), event);
	};

	const saveEventFlag = (event: SessionEvent) => {
		return saveSessionData(KnownTypes.SESSION_EVENT, event);
	};

	const getSessionExtraByName = async (name: string) => {
		//mimics what old next api was doing
		const data = await getSessionData<SessionRecord<SessionExtra>>(KnownTypes.SESSION_EXTRA, {
			params: {
				showPII: true
			}
		});
		return data.items?.filter((x) => x.record.data.name === name);
	};

	const saveSessionExtra = (event: SessionExtra) => {
		return saveSessionData(KnownTypes.SESSION_EXTRA, event);
	};

	const saveQuestionnaireProgress = (event: QuestionnaireProgressData["data"]) => {
		return saveSessionData(KnownTypes.T2T_QUESTIONNAIRE_PROGRESS, event);
	};

	const saveQuestionnaireReport = (event: QuestionnaireReport) => {
		return saveSessionData(KnownTypes.T2T_QUESTIONNAIRE_REPORT, event);
	};

	const getInProgressQuestionnaireResponses = async (params?: {
		questionnaireID?: string;
		language?: Language;
		version?: string;
	}) => {
		const questionnaireResponses = (
			await getSessionData<SessionRecord<QuestionnaireProgressData["data"]>>(KnownTypes.T2T_QUESTIONNAIRE_PROGRESS)
		).items
			?.filter((questResponse) => {
				let match = true;
				if (!isNil(params?.questionnaireID)) {
					match = match && questResponse?.record?.data?.questionnaireID === params.questionnaireID;
				}

				if (!isNil(params?.language)) {
					match = match && questResponse?.record?.data["language"] === params.language;
				}

				if (!isNil(params?.version)) {
					match = match && questResponse?.record?.data["version"] === params.version;
				}

				return match;
			})
			?.sort((a, b) => Date.parse(b.createdAt || "") - Date.parse(a.createdAt || ""));

		const questionAnswerMappings: { [key: string]: QuestionnaireEntry } = {};

		for (const questResponse of questionnaireResponses) {
			if (isNil(questResponse?.record?.data?.question?.questionID)) {
				continue;
			}

			if (isNil(questionAnswerMappings[questResponse.record.data.question.questionID])) {
				questionAnswerMappings[questResponse.record.data.question.questionID] = {
					linkID: questResponse.record.data.question.questionID,
					value: questResponse.record.data.question.answer,
					text: questResponse.record.data.question.title
				} as QuestionnaireEntry;
			}
		}

		return Object.values(questionAnswerMappings);
	};

	const getQuestionnaireResponses = async (params?: {
		questionnaireID?: string;
		language?: Language;
		version?: string;
		fallbackToInProgress?: boolean;
	}): Promise<QuestionnaireEntry[]> => {
		const reports = await getSessionData<SessionRecord<QuestionnaireReportData["data"]>>(
			KnownTypes.T2T_QUESTIONNAIRE_REPORT
		);

		const questionnaireReport = findLast(reports?.items, (item) => {
			return (
				!isNil(
					item.record.data.source?.identifiers?.find((identifier) => {
						return identifier.system.endsWith("emed.com/questionnaire") && identifier.value === params?.questionnaireID;
					})
				) &&
				item.record.data["language"] === params?.language &&
				item.record.data["version"] === params?.version
			);
		});

		if (isNil(questionnaireReport)) {
			if (params?.fallbackToInProgress) {
				return getInProgressQuestionnaireResponses(params);
			}

			return [];
		}

		let responses: QuestionnaireEntry[] = [];
		let questionnaireData: QuestionnaireDocumentMeta & { item: QuestionnaireItem[] };

		questionnaireData = JSON.parse(questionnaireReport.record?.data?.source?.data);
		responses = questionnaireData?.item?.map((q) => {
			const answer = questionnaireReport.record.data.items.find((item) => item.linkID === q.linkId);

			return {
				linkID: q.linkId,
				value: answer?.value,
				text: q.text
			} as QuestionnaireEntry;
		});

		return responses;
	};

	const processRecognize = (event: IUserImageRecognitionRequestBody) => {
		return callAPI<IUserImageRecognitionRequestBody, IImagingResult>("POST", "/v1/imaging/$recognize", {
			data: event
		});
	};

	const getOralToxExtra = async () => {
		return callAPI<void, IOralToxExtra>("GET", "/v1/imaging/$oral-tox");
	};

	const processOralTox = (event: IUserImageRecognitionRequestBody, attachmentID: string, procedureID: string) => {
		return callAPI<IUserImageRecognitionRequestBody, IImagingResult>("POST", "/v1/imaging/$oral-tox", {
			params: {
				attachmentID,
				procedureID
			},
			data: event
		});
	};

	const getFormResponse = (formName: string) => {
		return getLatestSessionData<SessionRecord<any, string>>(prefixFormType(formName));
	};
	const saveLegacyResult = (event: SaveLegacyResultRequest) => {
		//@ts-ignore not a KnownType obviously
		return saveSessionData(`test_result/${event.result.procedureID}`, { data: event });
	};

	const sendHeartbeat = async (): Promise<void> => {
		try {
			if (!AuthCtx.token) {
				logger.warn("sendHeartbeat", "No auth token available, skipping heartbeat");
				return;
			}
			return await callAPI<void, void>("POST", `/v1/session/$heartbeat`);
		} catch (e) {
			// Check if it's the specific "INVALID_MEETING_ID" error - this is expected when meeting ends
			if (e.response && e.response.status === 400 && e.response.data?.code === "INVALID_MEETING_ID") {
				logger.info("sendHeartbeat", "Meeting has ended, heartbeat no longer valid", {
					code: e.response.data.code,
					message: e.response.data.message
				});
				// Re-throw this specific error so the caller can handle cleanup
				throw e;
			}

			// Check if it's any other 4xx error (client error)
			if (e.response && e.response.status >= 400 && e.response.status < 500) {
				logger.error("sendHeartbeat", "4xx error received, session may be invalid", e, {
					status: e.response.status,
					statusText: e.response.statusText
				});
				// Re-throw the error so the caller can handle it
				throw e;
			}

			// For other errors (network, 5xx, etc.), just log and don't re-throw
			logger.error("sendHeartbeat", `Unable to send session heartbeat: ${e.message}`, e);
		}
	};

	const getAttachments = async (attachmentID: string): Promise<AttachmentRecordCollection> => {
		return await callAPI<any, AttachmentRecordCollection>(
			"GET",
			`/v1/session/attachments/${AuthCtx.sessionID}/${attachmentID}`
		);
	};

	const getLatestAttachment = async (attachmentID: string): Promise<AttachmentRecord> => {
		return await callAPI<any, AttachmentRecord>(
			"GET",
			`/v1/session/attachments/${AuthCtx.sessionID}/${attachmentID}/$latest`
		);
	};

	const createAttachment = async (request: CreateAttachmentRequest): Promise<AttachmentRecord> => {
		try {
			if (!AuthCtx.token) {
				throw new Error("Expected AuthCtx.token");
			}

			const formData = new FormData();
			const file = new File([request.blob], request.fileName, { type: request.contentType });
			formData.append("file", file, request.fileName);

			return (
				await axios.post<any, { data?: LoveResponse<AttachmentRecord> }>(
					`${VLabAPICtx.getAPIBaseUrl()}/v1/session/attachments/${AuthCtx.sessionID}/${request.attachmentID}`,
					formData,
					{
						headers: {
							"Content-Type": `multipart/form-data`,
							"x-api-key": AuthCtx.token
						},
						onUploadProgress: (progressEvent: AxiosProgressEvent) => {
							if (request.onUploadProgress) {
								request.onUploadProgress(progressEvent);
							}
						},
						timeout: EnvCtx.get(Config.ATTACHMENT_UPLOAD_TIMEOUT)
					}
				)
			)?.data?.with;
		} catch (error) {
			logger.error("createAttachment", `Unable to create attachment: ${error.message}`, error);
			throw error;
		}
	};

	const textExtractDrivingLicenceInfo = async (blob: Blob): Promise<TextractDrivingLicenceInfoResponse> => {
		try {
			if (!AuthCtx.token) {
				throw new Error("Expected AuthCtx.token");
			}

			const formData = new FormData();
			const file = new File([blob], "user_driving_license.jpg", { type: "image/jpeg" }); // TODO: get the correct mime type
			formData.append("file", file, "user_driving_license.jpg");

			const response = await axios.post(`${VLabAPICtx.getAPIBaseUrl()}/v1/textract/recognize-document`, formData, {
				headers: {
					"Content-Type": `multipart/form-data`,
					"x-api-key": AuthCtx.token
				}
			});

			return response.data.with;
		} catch (error) {
			logger.error("textExtractDrivingLicenceInfo", `Unable to textract driving licence info: ${error.message}`, error);
			throw error;
		}
	};

	const getDiagnosticComponents = async (procedureID: string) => {
		//mimics what old next api was doing
		const data = await getSessionData<SessionRecord<DiagnosticComponent>>(KnownTypes.DIAGNOSTIC_COMPONENT, {
			params: {
				showPII: true
			}
		});
		return data.items
			?.filter((x) => x.record.data.lab === procedureID)
			.sort((a, b) => Date.parse(a.createdAt || "") - Date.parse(b.createdAt || ""));
	};

	const saveDiagnosticComponent = async (event: DiagnosticComponent) => {
		return saveSessionData(KnownTypes.DIAGNOSTIC_COMPONENT, event);
	};

	const getDiagnosticResult = async (procedureID: string) => {
		//mimics what old next api was doing
		const data = await getSessionData<SessionRecord<DiagnosticReport>>(KnownTypes.DIAGNOSTIC_RESULT, {
			params: {
				showPII: true
			}
		});

		return data.items
			?.filter((x) => x.record.data.lab === procedureID)
			.sort((a, b) => Date.parse(a.createdAt || "") - Date.parse(b.createdAt || ""))
			.pop()?.record;
	};

	const getDeviceContent = (procedure_id: string, language?: string) => {
		return callAPI<any, DeviceConfiguration[]>("GET", `/v1/config/labs/${procedure_id}/devices`, {
			headers: {
				...(language && { "accept-language": language })
			}
		});
	};

	const getLabDefinitions = (language?: string) => {
		return callAPI<any, DiagnosticConfiguration>("GET", `/v1/config/labs`, {
			headers: {
				...(language && { "accept-language": language })
			}
		});
	};

	const getLabContent = (procedure_id: string, language?: string) => {
		return callAPI<any, LabConfiguration>("GET", `/v1/config/labs/${procedure_id}`, {
			headers: {
				...(language && { "accept-language": language })
			}
		});
	};

	const saveDiagnosticResult = (event: Omit<DiagnosticReport, "submittedBy" | "certifiedBy" | "time">) => {
		return saveSessionData(KnownTypes.DIAGNOSTIC_RESULT, event);
	};

	const saveChatMessage = (event: ChatMessageRequest): Promise<void> => {
		return saveSessionData(KnownTypes.CHAT, event);
	};

	const saveFeedback = (event: FeedbackRequest): Promise<void> => {
		return saveSessionData(KnownTypes.FEEDBACK, event);
	};

	const getProfilePrescription = async (): Promise<any> => {
		//not supported yet
		throw new Error("not found");
	};

	const getInsurancePreference = async (): Promise<InsuranceRecord | null> => {
		//not supported yet
		throw new Error("not found");
	};

	const saveInsurancePreference = async (record: InsuranceRecord): Promise<void> => {
		//not supported yet
		throw new Error("not found");
	};

	const getHealthieInsurancePlans = async (keywords: string): Promise<HealthieInsurancePlan[]> => {
		//not supported yet - we need to hit healthy api - default to empty array
		return [];
	};

	const saveLoggingEvent = (data: LoggingRecord) => {
		if (data?.application && /\/v\d+$/.test(data.application)) {
			data.application = data.application.replace(/\/v\d+$/, "");
		}

		const dataToSend: LoggingRecord = {
			...data,
			meta: {
				...data.meta,
				isMobile: isMobile({ tablet: true }),
				browser: navigator.userAgent
			}
		};
		return saveSessionData(KnownTypes.LOGGING, dataToSend, {
			params: {
				archiveOnly: true
			}
		});
	};

	const saveCheckpointEvent = async (procedureID: string, checkpointID: CheckpointID) => {
		const procedureName = await ProcedureScriptUtils.resolveScriptName(procedureID);
		const procedureVersion = await ProcedureScriptUtils.resolveScriptVersion(procedureID);

		if (checkpointID?.name === "test_finished") {
			const checkpoints = await getSessionData<SessionRecord<CheckpointRecord>>(KnownTypes.CHECKPOINT, {
				params: {
					showPII: true
				}
			});

			if (
				checkpoints.items.find((checkpoint) => checkpoint.record.data.name === `${procedureName}/${checkpointID.name}`)
			) {
				return;
			}
		}

		let checkpointRequest: CheckpointRecord = {
			source: null,
			name: `${procedureName}/${checkpointID.name}`,
			exit: checkpointID.exit
		};

		if (checkpointID?.name === "test_started") {
			checkpointRequest["procedureVersion"] = procedureVersion;
		}

		return await ExecutorUtils.retryWithBackoff(
			async () => {
				await saveSessionData(KnownTypes.CHECKPOINT, checkpointRequest);
			},
			1000,
			3
		).promise();
	};

	const saveDeviceFingerprint = async () => {
		const fingerprint = await DeviceFingerprintUtils.getDeviceFingerprint();
		return saveSessionData(KnownTypes.USER_DEVICE, fingerprint, {
			params: {
				showPII: true
			}
		});
	};

	const loadDeviceFingerprint = async () => {
		return getLatestSessionData(KnownTypes.USER_DEVICE);
	};

	const saveScanStatus = (record: ScannerStatusRequest): Promise<ScannerData> => {
		return callAPI<ScannerStatusRequest, ScannerData>("POST", "/v1/scanner/$status", { data: record });
	};

	const saveScanBurn = (record: ScannerData): Promise<void> => {
		return callAPI<ScannerData, void>("POST", "/v1/scanner/$burn", { data: record });
	};

	const saveScanRecheck = (record: ScannerData): Promise<void> => {
		return callAPI<ScannerData, void>("POST", "/v1/scanner/$recheck", { data: record });
	};

	const saveScanRecord = (record: ScannerData): Promise<void> => {
		return callAPI<ScannerData, void>("POST", "/v1/scanner/$record", { data: record });
	};

	const saveDataByType = (data: any, dataType: KnownTypes | string): Promise<void> => {
		return saveSessionData(dataType, data);
	};

	const getDataByType = (dataType: KnownTypes | string) => {
		return getLatestSessionData<SessionRecord<any, typeof dataType>>(dataType);
	};

	const isCompleted = async (): Promise<boolean> => {
		return await Plugin.current!.isCompleted();
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const callScanner = <T, R>(payload: IScannerPayload<T>): Promise<ScannerData<R>> => {
		if (payload.code) {
			return callAPI<IScannerPayload<T>, ScannerData<R>>("POST", "/v2/scanner", { data: payload });
		} else {
			return callAPI<IScannerPayload<T>, ScannerData<R>>("GET", "/v2/scanner", {
				params: {
					name: payload.name
				}
			});
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const assignAttributes = <T, R>(payload: IScannerPayload<T>): Promise<IScannerPayload<R>> => {
		return callAPI<IScannerPayload<T>, IScannerPayload<R>>("POST", "/v2/scanner/assign-attributes", {
			data: payload
		});
	};

	const saveVoucherVerify = (record: ScannerData): Promise<VerifyVoucherResponse> => {
		return callAPI<{ code: string }, VerifyVoucherResponse>("POST", "/v1/voucher/$verify", { data: record });
	};

	const saveVoucherError = (record: { error: VoucherVerifyErrorTypeExtended }): Promise<void> => {
		return callAPI<{ error: VoucherVerifyErrorTypeExtended }, void>("POST", "/v1/voucher/$error", { data: record });
	};

	const getVoucherStatus = (): Promise<VoucherStatusResponse> => {
		//TODO: should be a GET, api was setup with POST
		return callAPI<void, VoucherStatusResponse>("POST", "/v1/voucher/$status");
	};

	const processChatGptExtractWeight = (event: { base64Image?: string; fullTranscript?: string }) => {
		return callAPI<{ base64Image?: string; fullTranscript?: string; sessionId: string }, ChatGPTCompletionResponse>(
			"POST",
			"/v1/chatgpt/$extract-weight",
			{ data: { ...event, sessionId: AuthCtx.sessionID } }
		);
	};

	const processChatGptAudioTranscriptions = async (request: {
		blob: Blob;
		fileName: string;
		contentType: string;
	}): Promise<{ text: string }> => {
		try {
			if (!AuthCtx.token) {
				throw new Error("Expected AuthCtx.token");
			}

			const formData = new FormData();
			const file = new File([request.blob], request.fileName, { type: request.contentType });
			formData.append("file", file, request.fileName);

			return (
				await axios.post<any, { data?: LoveResponse<{ text: string }> }>(
					`${VLabAPICtx.getAPIBaseUrl()}/v1/chatgpt/audio/transcriptions`,
					formData,
					{
						headers: {
							"Content-Type": `multipart/form-data`,
							"x-api-key": AuthCtx.token
						},
						timeout: EnvCtx.get(Config.ATTACHMENT_UPLOAD_TIMEOUT)
					}
				)
			)?.data?.with;
		} catch (error) {
			logger.error(
				"processChatGptAudioTranscriptions",
				`Unable to send audio file to chatgpt: ${error.message}`,
				error
			);
			throw error;
		}
	};

	const saveMetric = (event: MetricRecord) => {
		return saveSessionData(KnownTypes.METRIC, {
			type: KnownTypes.METRIC,
			data: event
		});
	};

	const value: SessionDataContext = {
		authContext: AuthCtx,
		lookup: TransCtx.lookup,
		authToken: AuthCtx.token,
		binahContext: BinahCtx,
		env: EnvCtx,
		VLabAPICtx,
		ThemeCtx,
		Modal,
		sendHeartbeat,
		getDeviceContent,
		getLabDefinitions,
		getLabContent,
		getSessionMetadata,
		getPatientAddress,
		savePatientAddress,
		saveConsent,
		saveEventFlag,
		getSessionExtraByName,
		saveSessionExtra,
		saveMetric,
		saveQuestionnaireProgress,
		saveQuestionnaireReport,
		getQuestionnaireResponses,
		processRecognize,
		getOralToxExtra,
		processOralTox,
		getFormResponse,
		saveForm,
		saveLegacyResult,
		getAttachments,
		getLatestAttachment,
		createAttachment,
		getDiagnosticComponents,
		saveDiagnosticComponent,
		getDiagnosticResult,
		saveDiagnosticResult,
		saveChatMessage,
		saveFeedback,
		saveLoggingEvent,
		saveCheckpointEvent,
		saveDeviceFingerprint,
		loadDeviceFingerprint,
		getInsurancePreference,
		saveInsurancePreference,
		textExtractDrivingLicenceInfo,
		getHealthieInsurancePlans,
		saveScanStatus,
		saveScanBurn,
		saveScanRecheck,
		saveScanRecord,
		saveDataByType,
		getDataByType,
		isCompleted,
		callScanner,
		assignAttributes,
		saveVoucherVerify,
		saveVoucherError,
		getProfilePrescription,
		getVoucherStatus,
		processChatGptExtractWeight,
		processChatGptAudioTranscriptions
	};
	return (
		<SessionDataContext.Provider value={value}>
			<Suspense>
				<SessionDataPlugin ref={Plugin}>{props.children}</SessionDataPlugin>
			</Suspense>
		</SessionDataContext.Provider>
	);
};

function prefixFormType(formName: string): string {
	return `form/${formName}`;
}
